import { useEffect, useState } from 'preact/hooks';
import { createPortal } from 'preact/compat';
import { api, ApiError } from '../api/client';
import { IconCancel, IconSave, IconDeleteHard } from '../icons/HvacIcons';
import './hv-modal.css';

interface PlantMetadata {
  id: string;
  displayName: string;
  latitude?: number | null;
  longitude?: number | null;
  address?: string | null;
}

interface Props {
  plant: PlantMetadata;
  /** Called after a successful save. The parent should update its own state
   *  from the returned payload (or refetch the plant list). */
  onSaved(patch: PlantMetadata): void;
  onDeleted(): void;
  onClose(): void;
}

/**
 * Rename + metadata + delete for a single Plant. Uses the same modal shell
 * as LeafConfigModal for visual consistency. Delete is soft (server sets
 * deleted_at) and cascades to devices/nodes/leaves.
 */
export function PlantMetadataModal({ plant, onSaved, onDeleted, onClose }: Props) {
  const [displayName, setDisplayName] = useState(plant.displayName);
  const [latitude, setLatitude] = useState(plant.latitude?.toString() ?? '');
  const [longitude, setLongitude] = useState(plant.longitude?.toString() ?? '');
  const [address, setAddress] = useState(plant.address ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose]);

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const body: Record<string, unknown> = {};
      const clear: string[] = [];
      const dn = displayName.trim();
      if (dn.length === 0) throw new Error('Anzeigename darf nicht leer sein.');
      body.displayName = dn;

      const lat = latitude.trim();
      if (lat === '') clear.push('latitude');
      else {
        const n = Number(lat);
        if (!Number.isFinite(n)) throw new Error('Latitude ist keine gültige Zahl.');
        body.latitude = n;
      }
      const lon = longitude.trim();
      if (lon === '') clear.push('longitude');
      else {
        const n = Number(lon);
        if (!Number.isFinite(n)) throw new Error('Longitude ist keine gültige Zahl.');
        body.longitude = n;
      }
      const adr = address.trim();
      if (adr === '') clear.push('address');
      else body.address = adr;
      if (clear.length > 0) body.clearFields = clear;

      const res = await api.patch<PlantMetadata>(`/api/plants/${plant.id}`, body);
      onSaved(res);
    } catch (e) {
      if (e instanceof ApiError) {
        const bodyMsg = (e.body as { error?: string })?.error;
        setError(bodyMsg ?? `Speichern fehlgeschlagen (${e.status}).`);
      } else if (e instanceof Error) {
        setError(e.message);
      } else {
        setError('Unbekannter Fehler beim Speichern.');
      }
    } finally {
      setSaving(false);
    }
  };

  const del = async () => {
    setSaving(true);
    setError(null);
    try {
      await api.delete(`/api/plants/${plant.id}`);
      onDeleted();
    } catch (e) {
      const msg = e instanceof ApiError ? `Löschen fehlgeschlagen (${e.status}).` : 'Unbekannter Fehler.';
      setError(msg);
      setSaving(false);
    }
  };

  return createPortal(
    <div class="hv-modal-backdrop" onClick={onClose}>
      <div class="hv-modal" onClick={(e) => e.stopPropagation()}>
        <div class="hv-modal__header">
          <h2 class="hv-modal__title">Anlage bearbeiten</h2>
          <button type="button" class="hv-modal__close" onClick={onClose} aria-label="Schließen">×</button>
        </div>
        <div class="hv-modal__body">
          <div class="hv-modal__field">
            <label>Anzeigename</label>
            <input type="text" value={displayName} onInput={(e) => setDisplayName((e.target as HTMLInputElement).value)} />
          </div>
          <div class="hv-modal__field">
            <label>Anschrift</label>
            <input type="text" value={address} onInput={(e) => setAddress((e.target as HTMLInputElement).value)} placeholder="optional" />
          </div>
          <div class="hv-modal__row">
            <div class="hv-modal__field">
              <label>Latitude</label>
              <input type="text" value={latitude} onInput={(e) => setLatitude((e.target as HTMLInputElement).value)} placeholder="-90 bis 90" />
            </div>
            <div class="hv-modal__field">
              <label>Longitude</label>
              <input type="text" value={longitude} onInput={(e) => setLongitude((e.target as HTMLInputElement).value)} placeholder="-180 bis 180" />
            </div>
          </div>
          {error && <p class="hv-modal__error">{error}</p>}
        </div>
        <div class="hv-modal__footer">
          {confirmDelete ? (
            <>
              <span class="hv-modal__confirm-label">Wirklich löschen? Diese Anlage samt Devices, Nodes und Leaves wird soft-gelöscht.</span>
              <button type="button" class="hv-btn hv-btn--danger" onClick={del} disabled={saving}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><IconDeleteHard size={14} />Ja, löschen</span>
              </button>
              <button type="button" class="hv-btn" onClick={() => setConfirmDelete(false)} disabled={saving}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><IconCancel size={14} />Abbrechen</span>
              </button>
            </>
          ) : (
            <>
              <button type="button" class="hv-btn hv-btn--danger-outline" onClick={() => setConfirmDelete(true)} disabled={saving}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><IconDeleteHard size={14} />Anlage löschen</span>
              </button>
              <div style={{ flex: 1 }} />
              <button type="button" class="hv-btn" onClick={onClose} disabled={saving}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><IconCancel size={14} />Abbrechen</span>
              </button>
              <button type="button" class="hv-btn hv-btn--primary" onClick={save} disabled={saving}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><IconSave size={14} />Speichern</span>
              </button>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
